import { defineStore } from 'pinia';
import defaultUserImg from '@/assets/images/user.png';
import ajax from '@/utils/Ajax';
import emitter from '@shared/utils/EventBus';
import defaultNoImg from '@/assets/images/no-image.png';

export const useGlobalStore = defineStore('global', {
  state: () => ({
    // Define your store variables here
    // https://pinia.vuejs.org/core-concepts/state.html
    project: 'sephora',
    default_user_img: defaultUserImg,
    default_no_img: defaultNoImg,
    user: null, //登入人的資訊
    token: window.localStorage.getItem('token') || null,
    chatroom_url: '',
    page_heading: '',
    is_mobile: false,
    is_tablet: false,
    loading: false,
    initSocial: [
      {
        icon: 'instagram',
        name: 'Instagram',
        handle: '',
        tag: 'ig',
        matched: false,
        backName: ['instagram', 'instagram_business', 'facebook_business'],
        show: true,
      },
      {
        icon: 'tiktok-pure',
        name: 'TikTok',
        handle: '',
        tag: 'tk',
        matched: false,
        backName: ['tikTok', 'tiktok_business'],
        show: true,
      },
      {
        icon: 'youTube',
        name: 'YouTube',
        handle: '',
        tag: 'yt',
        matched: false,
        backName: ['youTube', 'youtube', 'youtube_business'],
        show: true,
      },
    ],
    sortlink_content:
      'Includes data from affiliate shortlinks shared by the creator that go directly to the product page.',
    open_announcement: false,
    now_storefront:{
      id:null,
      url_name:null,
      merchant_display_name: '',
      is_link_only: false,
      is_enable_multi_brand: false,
    },
    merchantListCache: null,
    userMeCache: null,
    all_merchant_list: [],
    merchant_list: [], // resolved getMerchantList() data; source of truth for archived counts
    unread_count: 0,
    unread_announcement_count: 0,
    unread_message_count: 0,
    academyHaveData: false,
    tutorialQueue: [],
  }),

  getters: {
    // Define your getters here
    // https://pinia.vuejs.org/core-concepts/getters.html
    current_merchant: (state) =>
      state.merchant_list.find((m) => m.merchant_account_id === state.now_storefront.id) || null,
    has_archived_content() {
      const m = this.current_merchant;
      if (!m) return false;
      return (m.archived_post_count + m.archived_collection_count + m.archived_product_count + m.archived_link_count) > 0;
    },
  },

  actions: {
    // Define your actions here
    // https://pinia.vuejs.org/core-concepts/actions.html
    setPageHeading(heading) {
      this.page_heading = heading;
    },

    setIsMobile(isMobile) {
      this.is_mobile = isMobile;
    },

    setIsTablet(isTablet) {
      this.is_tablet = isTablet;
    },

    setAcademyHaveData(hasData) {
      this.academyHaveData = hasData;
    },

    // Adjust an archived count on the current merchant so the change is reflected
    // on every page that reads archived counts from the store.
    changeArchivedCount(field, delta) {
      const merchant = this.merchant_list.find((m) => m.merchant_account_id === this.now_storefront.id);
      if (merchant) merchant[field] = Math.max(0, (merchant[field] || 0) + delta);
    },

    setTutorialQueue(merchants) {
      this.tutorialQueue = [...merchants];
    },

    setToken(token) {
      this.token = token;
      window.localStorage.setItem('token', token);
    },

    clearToken() {
      this.token = null;
      window.localStorage.removeItem('token');
    },

    logout() {
      if (window.localStorage.original_token) {
        window.localStorage.setItem('token', window.localStorage.original_token);

        setTimeout(() => {
          window.localStorage.removeItem('original_token');
          window.location.replace('/');
        }, 100);
      } else {
        ajax
          .post(`logout`)
          .then((res) => {
            this.clearToken();
            window.location.replace('/login');
          })
          .catch((err) => {
            this.clearToken();
            window.location.replace('/login');
          });
      }
    },

    async getAnnouncementList(merchant_account_id = '', paged = 1, is_clear_cache = false) {
      // Create a cache key based on parameters
      if (is_clear_cache) this.announcementListCache = null;
      const cacheKey = `${merchant_account_id}-${paged}`;

      // Initialize promiseCache if it doesn't exist
      if (!this.promiseCache) {
        this.promiseCache = {};
      }

      // if there is a request in progress for this specific combination, return the cached promise
      if (this.promiseCache[cacheKey]) {
        return this.promiseCache[cacheKey];
      }

      try {
        const obj = {
          paged,
        };

        if (merchant_account_id) obj.merchant_account_id = merchant_account_id;

        // send request and cache the promise
        this.promiseCache[cacheKey] = ajax
          .get('/chatroom/get-announcement-list', {
            params: obj,
          })
          .then(async (response) => {
            if (!response.data.status === 'success') throw new Error('Network Error');
            return response.data.data;
          });

        return await this.promiseCache[cacheKey];
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        if (is_clear_cache) {
          // Clear specific cache or all cache based on is_clear_cache
          if (is_clear_cache === 'all') {
            this.promiseCache = {};
          } else {
            delete this.promiseCache[cacheKey];
          }
        }
      }
    },

    async getMerchantList(is_clear_cache = false) {
      // if there is a request in progress, return the cached promise
      // console.log('getMerchantList', is_clear_cache);
      if (is_clear_cache) this.merchantListCache = null;

      if (this.merchantListCache) {
        return this.merchantListCache;
      }

      try {
        // send request and cache the promise
        this.merchantListCache = ajax.get('/chatroom/get-merchant-list').then(async (response) => {
          if (!response.data.status === 'success') throw new Error('Network Error');
          this.unread_count = 0;
          this.unread_announcement_count = 0;
          this.unread_message_count = 0;

          response.data.data.sort((a, b) => {
            if (!a.last_message && !b.last_message) return 0;
            if (!a.last_message) return 1;
            if (!b.last_message) return -1;

            return new Date(b.last_message.created_at) - new Date(a.last_message.created_at);
          });

          // this.unread_count = response.data.data.reduce((acc, curr) => acc + curr.unread_announcement_count + curr.unread_message_count, 0);
          response.data.data.forEach((item) => {
            this.unread_count = this.unread_count + (item.unread_announcement_count + item.unread_message_count);
            this.unread_announcement_count = this.unread_announcement_count + item.unread_announcement_count;
            this.unread_message_count = this.unread_message_count + item.unread_message_count;
          });

          this.merchant_list = response.data.data;
          return response.data.data;
        });

        return await this.merchantListCache;
      } catch (error) {
        this.error = error;
        throw error;
      } finally {
        if (is_clear_cache) this.merchantListCache = null;
      }
    },

    async getUserMe(is_clear_cache = false) {
      if (window.localStorage.getItem('token') === null) {
        return null;
      }

      if (
        window.localStorage.getItem('CREATOR_PROTAL_Registor_Google') ||
        window.localStorage.getItem('REATOR_PROTAL_register_go_out')
      ) {
        return null;
      }

      // console.log('getUserMe', is_clear_cache);

      if (is_clear_cache) this.userMeCache = null;

      if (this.userMeCache) {
        return this.userMeCache;
      }

      try {
        this.userMeCache = ajax.get('/me').then(async (response) => {
          // console.log('getUserMe', response.data.data);

          if (!response.data.status === 'success') throw new Error('Network Error');
          response.data.data.merchant_accounts.forEach((account) => {
            account.is_link_only = (account.anchor_creator_platform === 'linkonly' || account.platform_type === 'linkonly') ? true : false;
          });

          this.user = response.data.data;
          emitter.emit('refresh-user');
          return response.data.data;
        });

        return await this.userMeCache;
      } catch (error) {
        this.clearToken();
        this.error = error;
        throw error;
      } finally {
        if (is_clear_cache) this.userMeCache = null;
      }
    },
  },
});
